import { useEffect, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuMoon, LuAlertTriangle, LuCheckCircle2 } from 'react-icons/lu';
import { FormProvider, useForm } from 'react-hook-form';
import dayjs from 'dayjs';
import CustomDatePicker from '@Common/CustomDatePicker';
import Toggle from '../../../../components/Toggle';
import Input from '@Common/input';
import { FORM_REGEX_VALIDATORS } from '@utils';
import PlaceholderImage from '../../../channels/components/UnitsMapping/PlaceholderImage';
import type { IReservationDetail, IExtendStayValidateResult } from '../../types';


interface ExtendStayTabProps {
  detail: IReservationDetail;
  newCheckoutDate: string;
  onDateChange: (date: string) => void;
  validationResult: IExtendStayValidateResult | null;
  isValidating: boolean;
  onSwitchToExtendWithMove: () => void;
  isRtl: boolean;
  propertyImage?: string;
  checkInDate?: string;
  checkOutDate?: string;
  platformSrc?: number;
  initialGuestEmail?: string;
  isDirectPaymentAccepted: boolean;
  onDirectPaymentChange: (checked: boolean) => void;
  guestEmail: string;
  onGuestEmailChange: (email: string) => void;
  onValidationChange: (isValid: boolean) => void;
}

interface IExtendStayForm {
  newCheckoutDate: string;
  guestEmail: string;
  isDirectPaymentAccepted: boolean;
}

const ExtendStayTab = ({
  detail,
  newCheckoutDate,
  onDateChange,
  validationResult,
  isValidating,
  onSwitchToExtendWithMove,
  isRtl,
  propertyImage,
  checkInDate,
  checkOutDate,
  platformSrc,
  initialGuestEmail,
  isDirectPaymentAccepted,
  onDirectPaymentChange,
  guestEmail,
  onGuestEmailChange,
  onValidationChange,
}: ExtendStayTabProps) => {
  const { t, i18n } = useTranslation();
  const isInitialSync = useRef(true);

  const methods = useForm<IExtendStayForm>({
    mode: 'onChange',
    defaultValues: {
      newCheckoutDate: newCheckoutDate || '',
      guestEmail: guestEmail || initialGuestEmail || '',
      isDirectPaymentAccepted: isDirectPaymentAccepted || false,
    },
  });

  useEffect(() => {
    onValidationChange(methods.formState.isValid);
  }, [methods.formState.isValid, onValidationChange]);

  // Sync parent value → local form (initial only)
  useEffect(() => {
    if (newCheckoutDate && isInitialSync.current) {
      methods.reset({ newCheckoutDate });
      isInitialSync.current = false;
    }
  }, [newCheckoutDate, methods]);

  // Watch local form changes → propagate to parent
  useEffect(() => {
    const subscription = methods.watch((values) => {
      const date = values.newCheckoutDate
        ? dayjs(values.newCheckoutDate as any).isValid()
          ? dayjs(values.newCheckoutDate as any).format('YYYY-MM-DD')
          : String(values.newCheckoutDate)
        : '';
      if (date !== newCheckoutDate) {
        onDateChange(date);
      }
      if (values.guestEmail !== guestEmail) {
        onGuestEmailChange(values.guestEmail || '');
      }
      if (values.isDirectPaymentAccepted !== isDirectPaymentAccepted) {
        onDirectPaymentChange(!!values.isDirectPaymentAccepted);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods, onDateChange, newCheckoutDate, guestEmail, onGuestEmailChange, isDirectPaymentAccepted, onDirectPaymentChange]);

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Current reservation info card */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontSize: '14px',
              color: '#8E98A1',
            }}
          >
            {t('TaskManagemnt.currentStayPeriod')}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            {/* Property image */}
            <Box
              sx={{
                width: '80px',
                height: '60px',
                borderRadius: '8px',
                backgroundColor: '#E8E8EA',
                flexShrink: 0,
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {propertyImage ? (
                <Box
                  component="img"
                  src={propertyImage}
                  alt={detail.propertyName}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <PlaceholderImage />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#0F172A',
                }}
              >
                {detail.propertyName}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '12px',
                  color: '#8E98A1',
                }}
              >
                {checkInDate || detail.checkInDateDisplay} — {checkOutDate || detail.checkOutDateDisplay}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontSize: '12px',
                    color: '#8E98A1',
                  }}
                >
                  {detail.numberOfNights} {t('TaskManagemnt.nights')}
                </Typography>
                <LuMoon size={12} color="#8E98A1" />
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Date input */}
        <CustomDatePicker
          views={['day']}
          name="newCheckoutDate"
          label={t('TaskManagemnt.enterNewCheckoutDate')}
          format="YYYY-MM-DD"
          minDate={checkOutDate ? dayjs(checkOutDate) : undefined}
          withOutErrorMessage
        />

        {/* Dynamic Fields based on Platform SRC */}
        {validationResult?.isValid && !isValidating && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {platformSrc === 6 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E8E8EA',
                  backgroundColor: '#FFFFFF',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      color: '#0F172A',
                    }}
                  >
                    {t('MANUALRESERVATION.DIRECT_PAYMENT_RECEIPT')}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '12px',
                      color: '#8E98A1',
                    }}
                  >
                    {t('MANUALRESERVATION.PAYMENT_LINK_DESCRIPTION')}
                  </Typography>
                </Box>
                <Toggle
                  checked={methods.watch('isDirectPaymentAccepted')}
                  onChange={(_event: any, checked: boolean) => methods.setValue('isDirectPaymentAccepted', checked)}
                />
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: '16px',
                    borderRadius: '8px',
                    border: '1px solid #E8E8EA',
                    backgroundColor: '#FFFFFF',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '14px',
                        fontWeight: 700,
                        color: '#0F172A',
                      }}
                    >
                      {t('MANUALRESERVATION.DIRECT_PAYMENT_RECEIPT')}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: 'Tajawal, sans-serif',
                        fontSize: '12px',
                        color: '#8E98A1',
                      }}
                    >
                      {t('MANUALRESERVATION.PAYMENT_LINK_DESCRIPTION')}
                    </Typography>
                  </Box>
                  <Toggle
                    checked={methods.watch('isDirectPaymentAccepted')}
                    onChange={(_event: any, checked: boolean) => methods.setValue('isDirectPaymentAccepted', checked)}
                  />
                </Box>
                <Input
                  name="guestEmail"
                  label={t('MANUALRESERVATION.GUEST_EMAIL')}
                  placeholder={t('MANUALRESERVATION.GUEST_EMAIL')}
                  registerOptions={{
                    required: {
                      value: true,
                      message: t('MANUALRESERVATION.PLEASEENTERTHEGUESTSEMAIL'),
                    },
                    pattern: {
                      value: FORM_REGEX_VALIDATORS.email,
                      message: t('MANUALRESERVATION.PLEASEENTERTHEGUESTSEMAILCORRECTLY'),
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}


        {/* Validation loading */}
        {isValidating && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: '12px' }}>
            <div
              className="animate-spin rounded-full h-6 w-6"
              style={{ border: '2px solid #E8E8EA', borderTopColor: '#5B3FFF' }}
            />
          </Box>
        )}

        {/* Validation error */}
        {validationResult && !validationResult.isValid && !isValidating && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#FFF8F0',
                border: '1px solid #FFE0B2',
                borderRadius: '8px',
                p: '12px',
              }}
            >
              <LuAlertTriangle size={16} color="#FF8C00" style={{ flexShrink: 0 }} />
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontSize: '13px',
                  color: '#B45309',
                  flex: 1,
                }}
              >
                {i18n.language === 'ar'
                  ? (validationResult.messageAr || t('TaskManagemnt.extendError'))
                  : (validationResult.messageEn || t('TaskManagemnt.extendError'))}
              </Typography>
            </Box>
            <Typography
              onClick={onSwitchToExtendWithMove}
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '13px',
                color: '#5B3FFF',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { opacity: 0.8 },
              }}
            >
              {t('TaskManagemnt.availableUnitsLink')}
            </Typography>
          </Box>
        )}

        {/* Validation success */}
        {validationResult && validationResult.isValid && !isValidating && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#F0FFF4',
              border: '1px solid #C6F6D5',
              borderRadius: '8px',
              p: '12px',
            }}
          >
            <LuCheckCircle2 size={18} color="#38A169" style={{ flexShrink: 0 }} />
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontSize: '13px',
                color: '#276749',
                flex: 1,
              }}
            >
              {i18n.language === 'ar' ? validationResult.messageAr : validationResult.messageEn}
            </Typography>
          </Box>
        )}
      </Box>
    </FormProvider>
  );
};

export default ExtendStayTab;
